import React from "react";
import { observer } from "mobx-react";
import { FormikHelpers, useFormik } from "formik";
import * as yup from "yup";

import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";

import { useNotificationsStore } from "..";
import { NotificationReportCommand } from "api";

export const NotificationReportModal: React.FC<any> = observer(function (
  props
) {
  const { notificationGlobalStore, messageStore, ...store } =
    useNotificationsStore();

  const renderFooter = () => {
    return (
      <div>
        <Button label="Close" onClick={onHide} className="p-button-sm" />
        <Button
          label="Run"
          className="p-button-sm"
          onClick={(e) => {
            onBeforeSubmit();
            e.preventDefault();
          }}
          autoFocus
        />
      </div>
    );
  };

  const onHide = () => {
    notificationGlobalStore.showReportModal = false;
    onClear();
  };

  const formikProps = useFormik<NotificationReportCommand>({
    initialValues: notificationGlobalStore.emptyReportCriteria,
    validationSchema: yup.object().shape(
      {
        // startDate also cannot be after endDate
        startDate: yup
          .date()
          .required("Start Date is required")
          .nullable()
          .when("endDate", (endDate: string, schema: any) => {
            return endDate
              ? schema.max(
                  endDate,
                  "Start Date must be less than or equal to End Date"
                )
              : schema;
          }),

        // endDate also cannot be before startDate
        endDate: yup
          .date()
          .required("End Date is required")
          .nullable()
          .when("startDate", (startDate: string, schema: any) => {
            return startDate
              ? schema.min(
                  startDate,
                  "End Date must be greater than or equal to Start Date"
                )
              : schema;
          }),
      },
      [["startDate", "endDate"]]
    ),
    onSubmit: async (
      values: NotificationReportCommand,
      { setSubmitting }: FormikHelpers<NotificationReportCommand>
    ) => {
      store.loadNotificationsReport(values);
      setSubmitting(false);
      onHide();
    },
    validateOnBlur: false,
    validateOnChange: false,
    enableReinitialize: true,
  });

  const onBeforeSubmit = () => {
    formikProps.setValues({ ...formikProps.values });
    formikProps.handleSubmit();
  };

  const onClear = () => {
    formikProps.initialValues = notificationGlobalStore.emptyReportCriteria;
    formikProps.handleReset(null);
  };

  return (
    <>
      <Dialog
        header="Notifications Summary"
        footer={renderFooter}
        visible={notificationGlobalStore.showReportModal}
        breakpoints={{ "960px": "75vw" }}
        style={{ width: "15vw" }}
        onHide={onHide}
      >
        <div className="p-fluid p-grid p-formgrid p-mt-2 p-mb-2">
          <div className="p-field p-col-12">
            <Calendar
              id="startDate"
              name="startDate"
              placeholder="Start Date"
              showOnFocus={false}
              dateFormat={"mm/dd/yy"}
              value={formikProps.values.startDate}
              onChange={async (e) => {
                if (e) {
                  formikProps.setFieldValue("startDate", e.value);
                }
              }}
              className={
                formikProps.errors.startDate && formikProps.touched.startDate
                  ? "p-invalid"
                  : ""
              }
              showIcon
              showButtonBar
              monthNavigator
              yearNavigator
              yearRange="1950:2100"
            />
            {formikProps.errors.startDate && formikProps.touched.startDate && (
              <small id="startDateHelp" className="p-error">
                {formikProps.errors.startDate}
              </small>
            )}
          </div>
          <div className="p-field p-col-12">
            <Calendar
              id="endDate"
              name="endDate"
              placeholder="End Date"
              showOnFocus={false}
              dateFormat={"mm/dd/yy"}
              value={formikProps.values.endDate}
              onChange={async (e) => {
                if (e) {
                  formikProps.setFieldValue("endDate", e.value);
                }
              }}
              className={
                formikProps.errors.endDate && formikProps.touched.endDate
                  ? "p-invalid"
                  : ""
              }
              showIcon
              showButtonBar
              monthNavigator
              yearNavigator
              yearRange="1950:2150"
            />
            {formikProps.errors.endDate && formikProps.touched.endDate && (
              <small id="endDateHelp" className="p-error">
                {formikProps.errors.endDate}
              </small>
            )}
          </div>
          <div className="p-field-checkbox p-col-12">
            <Checkbox
              id="includeResolved"
              name="includeResolved"
              inputId="includeResolved"
              checked={formikProps.values.includeResolved}
              onChange={async (e) => {
                if (e) {
                  formikProps.setFieldValue("includeResolved", e.checked);
                }
              }}
            />
            <label htmlFor="includeResolved" className="p-checkbox-label">
              Include Resolved Notifications
            </label>
          </div>
        </div>
      </Dialog>
    </>
  );
});
